import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Location, Watch } from '../../assests/ExportImageIcon'
import loadScript from '../../utils/loadScript'
import { showToast } from '../../componants/toast/toastService'
import { setEventBookingConfirmation } from '../../redux/eventBooking/eventBookingSlice'
import { getStripePublishableKey } from '../../config/stripeEnv'

// Show the exact amount the API returned — never round it away.
const formatRupees = (value) => {
  const amount = Number(value)
  if (Number.isNaN(amount)) return '—'
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const formatScheduleDate = (schedule) => {
  if (!schedule?.event_date) return '—'
  const date = new Date(`${schedule.event_date}T00:00:00`)
  if (Number.isNaN(date.getTime())) return schedule.event_date
  return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
}

const formatTime = (value) => {
  if (!value) return ''
  const [hh, mm] = value.split(':')
  const hour = Number(hh)
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = ((hour + 11) % 12) + 1
  return `${displayHour}:${mm} ${period}`
}

const EventPayment = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const event = useSelector((state) => state.eventBooking.selectedEvent)
  const bookingGuests = useSelector((state) => state.eventBooking.bookingGuests)
  const bookingResult = useSelector((state) => state.eventBooking.bookingResult)

  const [fareSummaryOpen, setFareSummaryOpen] = useState(true)
  const [secondsLeft, setSecondsLeft] = useState(9 * 60 + 29)
  const [paying, setPaying] = useState(false)
  const [stripeReady, setStripeReady] = useState(false)
  const [stripeCardError, setStripeCardError] = useState('')

  const stripeRef = useRef(null)
  const elementsRef = useRef(null)
  const paymentElementRef = useRef(null)
  const mountTokenRef = useRef(0)

  const stripeKey = getStripePublishableKey()
  const clientSecret = String(bookingResult?.client_secret || '').trim()
  const paymentSummary = bookingResult?.payment_summary || null
  const currency = String(paymentSummary?.currency || 'INR').toUpperCase()
  const amountToPay = paymentSummary?.total_amount != null
    ? Number(paymentSummary.total_amount)
    : (bookingResult?.amount != null ? Number(bookingResult.amount) / 100 : null)

  const guestNames = useMemo(
    () => (bookingGuests || []).map((g) => [g.first_name, g.last_name].filter(Boolean).join(' ').trim()).filter(Boolean),
    [bookingGuests]
  )
  const primaryGuest = (bookingGuests || [])[0] || {}

  const bookedSchedule = bookingResult?.schedule || null
  const bookedItems = bookingResult?.items || []
  const venue = event?.event_venues?.[0]

  useEffect(() => {
    if (secondsLeft <= 0) return
    const timerId = setTimeout(() => setSecondsLeft((prev) => prev - 1), 1000)
    return () => clearTimeout(timerId)
  }, [secondsLeft])

  useEffect(() => {
    if (!clientSecret) return undefined
    let cancelled = false
    const token = ++mountTokenRef.current

    const mountStripe = async () => {
      setStripeReady(false)
      setStripeCardError('')

      if (!stripeKey) {
        setStripeCardError('Stripe publishable key not configured. Add REACT_APP_STRIPE_PUBLISHABLE_KEY to .env and restart the app.')
        return
      }

      try {
        const Stripe = await loadScript('https://js.stripe.com/v3/', 'Stripe')
        if (cancelled || token !== mountTokenRef.current) return
        stripeRef.current = Stripe(stripeKey)

        await new Promise((r) => window.setTimeout(r, 50))
        if (cancelled || token !== mountTokenRef.current) return

        const mountEl = document.getElementById('event-payment-element')
        if (!mountEl) {
          if (!cancelled) setStripeCardError('Stripe form container not ready. Refresh and try again.')
          return
        }
        mountEl.innerHTML = ''
        const elements = stripeRef.current.elements({
          clientSecret,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#026D4D',
              borderRadius: '12px',
              fontFamily: 'Onest, system-ui, sans-serif',
            },
          },
        })
        elementsRef.current = elements
        const paymentElement = elements.create('payment', {
          layout: { type: 'tabs', defaultCollapsed: false },
          paymentMethodOrder: ['card'],
          fields: { billingDetails: 'auto' },
        })
        paymentElement.mount('#event-payment-element')
        paymentElement.on('change', (event2) => {
          if (!cancelled) setStripeCardError(event2.error ? event2.error.message : '')
        })
        paymentElement.on('ready', () => {
          if (!cancelled && token === mountTokenRef.current) setStripeReady(true)
        })
        paymentElementRef.current = paymentElement

        window.setTimeout(() => {
          if (!cancelled && token === mountTokenRef.current) setStripeReady(true)
        }, 800)
      } catch (error) {
        if (!cancelled) {
          setStripeCardError(error?.message || 'Failed to load Stripe card form.')
          setStripeReady(false)
        }
      }
    }

    mountStripe()

    return () => {
      cancelled = true
      if (paymentElementRef.current) {
        try {
          paymentElementRef.current.unmount()
        } catch {
          // ignore unmount races
        }
        paymentElementRef.current = null
      }
      elementsRef.current = null
    }
  }, [clientSecret, stripeKey])

  const handlePayClick = async () => {
    if (paying || !stripeReady || !stripeRef.current || !elementsRef.current) return

    setPaying(true)
    try {
      const result = await stripeRef.current.confirmPayment({
        elements: elementsRef.current,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/event-booking-confirm`,
        },
      })
      if (result.error) throw new Error(result.error.message || 'Payment failed.')
      const intent = result.paymentIntent
      if (!intent || !['succeeded', 'processing', 'requires_capture'].includes(intent.status)) {
        throw new Error(`Stripe status: ${intent?.status || 'unknown'}`)
      }
      dispatch(setEventBookingConfirmation({
        ...bookingResult,
        paymentIntent: intent,
        payment_intent_id: intent.id,
        paidAt: new Date().toISOString(),
      }))
      showToast('Payment successful', 'success')
      navigate('/event-booking-confirm')
    } catch (error) {
      showToast(error?.message || 'Payment failed.', 'error')
    } finally {
      setPaying(false)
    }
  }

  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, '0')
  const seconds = String(secondsLeft % 60).padStart(2, '0')

  if (!bookingResult) {
    return (
      <section className="paymentPart commonTopPadding commonBottomPadding">
        <div className="container">
          <p>No event booking in progress. Please complete guest details first.</p>
          <button type="button" className="commonBtn" onClick={() => navigate('/event-booking-detail')}>
            Back to guest details
          </button>
        </div>
      </section>
    )
  }

  return (
    <>
        <section className='paymentPart commonTopPadding commonBottomPadding'>
            <div className='container'>
                <div className='row g-4'>
                    <div className='col-md-12'>
                        <div className='time bgWhite paddingCommon20 borderRadius40 commonBorder'>
                            <span className='timePill'>
                                <img src={Watch} alt="" />
                                {minutes} : {seconds}
                            </span>
                        </div>
                    </div>
                    <div className='col-md-12'>
                        <div className='paymentSec allBookingDetail'>
                            <div className='onlineCrad allBokkingInfo'>
                                <div className='bsSectionTitle'>Payment</div>
                                <p className='bsFareNote'>Enter your card details to complete this booking.</p>

                                <div className='paymentMethodBox'>
                                    <div className='paymentMethodPanel'>
                                        {stripeKey ? (
                                            <>
                                                <div id='event-payment-element' className='paymentStripeCard' />
                                                {stripeCardError && <p className='bsFareNote'>{stripeCardError}</p>}
                                                {!stripeReady && !stripeCardError && <p className='bsFareNote'>Loading secure card form…</p>}
                                            </>
                                        ) : (
                                            <div className='paymentInfoBanner'>
                                                Stripe key not loaded. Stop the dev server fully, then run{' '}
                                                <code>npm start</code> again so{' '}
                                                <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> from <code>.env</code> is available.
                                            </div>
                                        )}

                                        <button
                                            type='button'
                                            className='commonBtn w-100'
                                            onClick={handlePayClick}
                                            disabled={paying || !stripeReady}
                                        >
                                            {paying ? (
                                                <>
                                                    <span className='btnSpinner' aria-hidden="true" />
                                                    Processing…
                                                </>
                                            ) : (
                                                `Securely Pay ${amountToPay != null ? formatRupees(amountToPay) : ''}`
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className='paymentSidebar bookingSidebar'>
                                <div className='bsSection bgWhite paddingCommon20 borderRadius20 commonBorder'>
                                    <div className='fareDetailsHead' onClick={() => setFareSummaryOpen(!fareSummaryOpen)}>
                                        <div className='bsSectionTitle'>Fare Summary</div>
                                        <span className={`fareCollapseIcon ${fareSummaryOpen ? 'open' : ''}`}>&#8963;</span>
                                    </div>

                                    {fareSummaryOpen && (
                                        <>
                                            {paymentSummary ? (
                                                <>
                                                    <div className='bsFareRow mt-3'>
                                                        <span>Net Amount</span>
                                                        <span>{formatRupees(paymentSummary.net_amount)}</span>
                                                    </div>
                                                    <div className='bsFareRow'>
                                                        <span>Taxes &amp; Fees</span>
                                                        <span>{formatRupees(paymentSummary.taxes_and_fees)}</span>
                                                    </div>
                                                    <div className='bsFareRow'>
                                                        <span>Convenience Fee</span>
                                                        <span>{formatRupees(paymentSummary.convenience_fee)}</span>
                                                    </div>
                                                    {Number(paymentSummary.discount_amount) > 0 && (
                                                        <div className='bsFareRow bookingDiscountRow'>
                                                            <span>Discount</span>
                                                            <span>-{formatRupees(paymentSummary.discount_amount)}</span>
                                                        </div>
                                                    )}
                                                    <div className='bsFareDivider' />
                                                    <div className='bsFareRow bsFareRow--total'>
                                                        <span>Amount To Be Paid</span>
                                                        <span>{formatRupees(paymentSummary.total_amount)}</span>
                                                    </div>
                                                    <p className='bsFareNote'>Currency: {currency}</p>
                                                </>
                                            ) : (
                                                <div className='bsFareRow bsFareRow--total mt-3'>
                                                    <span>Amount To Be Paid</span>
                                                    <span>{amountToPay != null ? formatRupees(amountToPay) : '—'}</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {bookingResult?.booking_id && (
                                        <p className='bsFareNote'>Booking ref: {bookingResult.booking_id}</p>
                                    )}
                                </div>

                                <div className='bsSection'>
                                    <div className='bsBox'>
                                        <div className='hotelCard__topRow'>
                                            <div>
                                                <div className='hotelCard__name'>{event?.name || bookingResult?.event?.name}</div>
                                                {venue && (
                                                    <div className='hotelCard__location mt-2'>
                                                        <img src={Location} alt="" />
                                                        <div className='hotelCard__locationName'>{venue.venue_name}</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className='bsFareDivider' />

                                        <div className='bsFareRow mt-2'>
                                            <span>DATE</span>
                                            <span>{formatScheduleDate(bookedSchedule)}</span>
                                        </div>
                                        <div className='bsFareRow mt-2'>
                                            <span>TIME</span>
                                            <span>{bookedSchedule?.start_time ? formatTime(bookedSchedule.start_time) : '—'}</span>
                                        </div>
                                        {bookedItems.map((item) => (
                                            <div className='bsFareRow mt-2' key={item.id}>
                                                <span>{item.ticket_name}</span>
                                                <span>× {item.quantity}</span>
                                            </div>
                                        ))}

                                        <div className='bsFareDivider' />

                                        <div className='bsSectionTitle fareBaggageTitle'>Guests</div>
                                        <ul className='paymentTravellerList'>
                                            {guestNames.length ? guestNames.map((name, index) => (
                                                <li key={`${name}-${index}`}>{index + 1}. {name}</li>
                                            )) : (
                                                <li>Guest details unavailable</li>
                                            )}
                                        </ul>
                                        {primaryGuest.email && (
                                            <p className='bsFareNote'>{primaryGuest.email}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </>
  )
}

export default EventPayment
